import {
  BoundMultiSelectField,
  BoundNumberField,
  BoundSwitchField,
  BoundTextField,
  BoundTreeSelectField,
  column,
  Css,
  FormLines,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  Palette,
  simpleHeader,
  Tooltip,
} from "@homebound/beam";
import { useMemo } from "react";
import { Document, Page } from "react-pdf";
import { useParams } from "react-router-dom";
import { Icon, priceCell, UppyUploader } from "src/components";
import { SaveCommunityInput, SaveLotInput, SaveStructureInput } from "src/generated/graphqlTypes";
import { queryResult } from "src/utils";
import { ObjectConfig, useFormState } from "src/utils/formState";
import {
  type CheckoutSettingsTab_CommunityFragment,
  type CheckoutSettingsTab_LotFragment,
  type CheckoutSettingsTab_StructureFragment,
  useCheckoutSettingsTab_CommunityQuery,
  useCheckoutSettingsTab_LotQuery,
  useCheckoutSettingsTab_ProductOfferingOptionsQuery,
  useCheckoutSettingsTab_StructureQuery,
  useSaveCommunitySettingsMutation,
  useSaveLotSettingsMutation,
  useSaveStructureSettingsMutation,
} from "./CheckoutSettingsTab.generated";

type CheckoutSettingsTabProps = { projectType: "Community" | "Structure" | "Lot" };

export function CheckoutSettingsTab({ projectType }: CheckoutSettingsTabProps) {
  const { projectId } = useParams<{ projectId: string }>();

  const communityQuery = useCheckoutSettingsTab_CommunityQuery({
    variables: { projectId },
    skip: projectType !== "Community",
  });
  const structureQuery = useCheckoutSettingsTab_StructureQuery({
    variables: { projectId },
    skip: projectType !== "Structure",
  });
  const lotQuery = useCheckoutSettingsTab_LotQuery({ variables: { projectId }, skip: projectType !== "Lot" });

  if (projectType === "Community") {
    return queryResult(communityQuery, (d) => <CommunitySettingsTabDataView community={d.community} />);
  } else if (projectType === "Structure") {
    return queryResult(structureQuery, (d) => <StructureSettingsTabDataView structure={d.structure} />);
  } else if (projectType === "Lot") {
    return queryResult(lotQuery, (d) => <LotSettingsTabDataView lot={d.lot} />);
  } else {
    throw new Error(`Unhandled project type: ${projectType}`);
  }
}

function CommunitySettingsTabDataView({ community }: { community: CheckoutSettingsTab_CommunityFragment }) {
  const [saveCommunitySettings] = useSaveCommunitySettingsMutation();
  const formState = useFormState({
    config: communityFormConfig,
    init: {
      input: community,
      map: (community) => ({
        id: community.id,
        pricingTeaser: community.pricingTeaser,
        showInCheckout: community.showInCheckout,
        packageComparison: community?.packageComparison,
      }),
    },
    autoSave: async () => {
      await saveCommunitySettings({ variables: { input: formState.changedValue } });
    },
  });

  return (
    <FormLines width="sm">
      <BoundTextField field={formState.pricingTeaser} />
      <BoundSwitchField field={formState.showInCheckout} labelStyle="inline" />
      <div css={Css.sm.df.gap1.fdc.$}>
        <div css={Css.sm.df.gap1.aic.$}>
          <div>Upload Package Comparison Sheet</div>
          <Tooltip title="This pdf will appear as a link via the ‘Compare’ text on the ‘Interior Package’ step in Checkout. This lets users see the difference in finishes, materials, and details to choose the spec level they prefer ">
            <Icon icon="infoCircle" inc={2} color={Palette.Gray700} />
          </Tooltip>
        </div>
        {/* Rely on form autosave to toggle between upload and preview view */}
        {!!community.packageComparison ? (
          <div css={Css.relative.ba.bcGray100.br4.bw2.wminc.mhPx(60).df.$}>
            <Document file={community.packageComparison.downloadUrl} loading="" renderMode="svg">
              <div css={Css.w100.h100.$}>
                <Page
                  width={320}
                  loading=""
                  pageNumber={1}
                  renderMode="canvas"
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
              <div css={Css.absolute.top0.right0.$}>
                <IconButton
                  inc={5}
                  icon="x"
                  onClick={() => {
                    formState.packageComparison.set(undefined);
                  }}
                />
              </div>
            </Document>
          </div>
        ) : (
          <UppyUploader
            onFinish={async (file) => {
              formState.packageComparison.set({
                contentType: file.type,
                fileName: file.name,
                s3Key: file.meta.s3Key as string,
                sizeInBytes: file.data?.size ?? file.size ?? 0,
              });
            }}
            maxNumberOfFiles={1}
            dragDropWidth="250px"
            dragDropHeight="44px"
            dragDropText="Drag & drop file"
          />
        )}
      </div>
    </FormLines>
  );
}

function StructureSettingsTabDataView({ structure }: { structure: CheckoutSettingsTab_StructureFragment }) {
  const [saveStructureSettings] = useSaveStructureSettingsMutation();
  const productOfferingId = structure.readyPlanConfig?.readyPlan?.id;
  const currentOptions = useMemo(() => createOptionRows(structure), [structure]);
  // Get all options from the PRPC ready plan
  const optionsQuery = useCheckoutSettingsTab_ProductOfferingOptionsQuery({
    variables: { productOfferingId: productOfferingId! },
    skip: !productOfferingId,
  });
  const formState = useFormState({
    config: structureFormConfig,
    init: {
      input: structure,
      map: (structure) => ({
        id: structure.id,
        showInCheckout: structure.showInCheckout,
        includedPriceInCents: structure.readyPlanConfig?.includedPriceInCents,
        includedOptions: structure.readyPlanConfig?.includedOptions.map((opt) => opt.id) ?? [],
      }),
    },
    autoSave: async () => {
      await saveStructureSettings({ variables: { input: formState.changedValue } });
    },
  });
  // Build tree structure from optionGroups, filtering by active and showInCheckout
  const optionsTree = useMemo(() => {
    const optionGroups = optionsQuery.data?.productOffering.optionGroups ?? [];
    return optionGroups
      .filter((group) => group.active)
      .sortBy((group) => group.order)
      .map((group) => ({
        id: group.id,
        name: group.name,
        children: group.options
          .filter((option) => option.active && option.showInCheckout)
          .map((option) => ({ id: option.id, name: option.name })),
      }))
      .filter((group) => group.children.nonEmpty);
  }, [optionsQuery.data?.productOffering.optionGroups]);

  return (
    <FormLines width="sm">
      <BoundSwitchField field={formState.showInCheckout} />
      <BoundNumberField
        type="cents"
        field={formState.includedPriceInCents}
        disabled={structure.readyPlanConfig === undefined ? "Project does not have a configuration yet" : false}
      />
      <BoundTreeSelectField
        field={formState.includedOptions}
        label="Included Options"
        options={optionsTree}
        defaultCollapsed
        onSelect={(options) => formState.includedOptions.set(options.leaf.values)}
        placeholder="Select options"
        chipDisplay="root"
        disabled={!productOfferingId ? "Project does not have a ready plan" : false}
      />
      <div data-testid="currentOptions" css={Css.mt2.$}>
        <div css={Css.mb1.xsSb.$}>Current Options</div>
        <GridTable columns={currentOptionColumns} rows={currentOptions} />
      </div>
    </FormLines>
  );
}

type CurrentOptionHeaderRow = { kind: "header" };

type CurrentOptionRow = {
  kind: "option";
  id: string;
  data: NonNullable<CheckoutSettingsTab_StructureFragment["readyPlanConfig"]>["options"][number];
};

type CurrentOptionTableRow = CurrentOptionHeaderRow | CurrentOptionRow;

const currentOptionColumns: GridColumn<CurrentOptionTableRow>[] = [
  column<CurrentOptionTableRow>({
    header: "Option",
    option: ({ readyPlanOption }) => readyPlanOption.displayName,
    w: "560px",
  }),
  column<CurrentOptionTableRow>({
    header: () => (
      <div css={Css.df.aic.gap1.$}>
        Price
        <Tooltip title="Price Delta above the Included Option">
          <Icon icon="infoCircle" inc={1.5} color={Palette.Gray700} />
        </Tooltip>
      </div>
    ),
    option: ({ priceInCents }) => priceCell({ valueInCents: priceInCents }),
    w: "160px",
  }),
];

/** Creates current option rows sorted by their option group's checkout order. */
function createOptionRows(structure: CheckoutSettingsTab_StructureFragment): GridDataRow<CurrentOptionTableRow>[] {
  return [
    simpleHeader,
    ...(structure.readyPlanConfig?.options ?? [])
      .sortBy((option) => option.readyPlanOption.optionGroup.order)
      .map((option) => ({ kind: "option", id: option.id, data: option }) as const),
  ];
}

function LotSettingsTabDataView({ lot }: { lot: CheckoutSettingsTab_LotFragment }) {
  const [saveLotSettings] = useSaveLotSettingsMutation();
  const productOfferingOptions = useMemo(
    () => lot.parent.parent.productOfferings.map((po) => ({ id: po.id, name: po.name })),
    [lot],
  );

  const formState = useFormState({
    config: lotFormConfig,
    init: { input: lot, map: (lot) => ({ id: lot.id, buildablePlanIds: lot.buildablePlans.map(({ id }) => id) }) },
    autoSave: async ({ changedValue }) => {
      await saveLotSettings({ variables: { input: changedValue } });
    },
  });

  return (
    <FormLines width="sm">
      <BoundMultiSelectField
        field={formState.buildablePlanIds}
        label="Buildable Plans"
        options={productOfferingOptions}
        getOptionLabel={(o) => o.name}
        getOptionValue={(o) => o.id}
      />
    </FormLines>
  );
}

const communityFormConfig: ObjectConfig<SaveCommunityInput> = {
  id: { type: "value" },
  pricingTeaser: { type: "value" },
  showInCheckout: { type: "value" },
  packageComparison: { type: "value" },
};

const structureFormConfig: ObjectConfig<SaveStructureInput> = {
  id: { type: "value" },
  showInCheckout: { type: "value" },
  includedPriceInCents: { type: "value" },
  includedOptions: { type: "value" },
};

const lotFormConfig: ObjectConfig<SaveLotInput> = { id: { type: "value" }, buildablePlanIds: { type: "value" } };
